/**
 * React Query Persistence with IndexedDB
 *
 * Spotify-model persistence:
 * - Per-user IndexedDB storage (key includes userId)
 * - GRANULAR allowlist (not domain-only) to prevent cache bloat
 * - Size-bounded cache with automatic trimming
 * - Hydration waits for auth to resolve
 *
 * Granular allowlist:
 * - rating.one (individual ratings) - YES, 30 days
 * - ratings.list (all ratings) - NO (too large, refetch on demand)
 * - notifications.unreadCount - YES, 10 minutes
 * - notifications dropdown (limit ≤ 10, cursor null) - YES, 1 hour (cache-first UX)
 * - notifications infinite feed - NO (always refetch)
 * - social feed (limit ≤ 20, cursor null) - YES, 30 minutes (cache-first)
 * - lists, profiles, media details - YES with appropriate maxAge
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/plugins/persistQueryClient
 */

import { get, set, del, createStore, type UseStore } from 'idb-keyval';
import type { PersistedClient, Persister } from '@tanstack/react-query-persist-client';
import type { Query, DehydratedState, QueryKey } from '@tanstack/react-query';
import { isDefaultDiscoverParams, type DiscoverParams } from './queryKeys';

// Persistence version - bump to invalidate all persisted caches
const PERSISTENCE_VERSION = 7; // Bumped: increase shelf cap 12→20 for social shelves, invalidate stale caches

// Cache bounds
const MAX_ENTRIES = 500;
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10MB per user

// maxAge per query type (in ms)
// Persisted data older than this won't be rehydrated
const MAX_AGE = {
  // User's lists metadata - persist for 7 days (changes infrequently once loaded)
  LISTS: 7 * 24 * 60 * 60 * 1000,
  // List items (canonical variant only) - 30 minutes (prevents stale removed-item ghosts)
  LISTS_ITEMS: 30 * 60 * 1000,
  // Individual ratings - persist for 30 days (rarely changes)
  RATING_ONE: 30 * 24 * 60 * 60 * 1000,
  // Notifications unread count - 10 minutes (badge needs to stay fresh)
  NOTIFICATIONS_UNREAD: 10 * 60 * 1000,
  // Notifications dropdown list - 1 hour (longer for cache-first UX, SWR keeps fresh)
  NOTIFICATIONS_LIST: 60 * 60 * 1000,
  // Profiles - persist for 3 days
  PROFILE: 3 * 24 * 60 * 60 * 1000,
  // Watchlist follows - persist for 3 days
  WATCHLIST_FOLLOWS: 3 * 24 * 60 * 60 * 1000,
  // Media detail pages - persist for 1 day
  MEDIA_DETAIL: 24 * 60 * 60 * 1000,
  // Discover grid (default filters only) - persist for 1 hour
  DISCOVER: 60 * 60 * 1000,
  // Social feed (first page only) - persist for 30 minutes
  SOCIAL: 30 * 60 * 1000,
  // Recent searches - persist for 30 days (bounded: max 6 items, Spotify-grade instant display)
  RECENT_SEARCHES: 30 * 24 * 60 * 60 * 1000,
  // Users followers/following - persist for 10 minutes (bounded: first page only, trimmed to 20 items)
  USERS_FOLLOWS: 10 * 60 * 1000,
  // Public profile lists (other users) - persist for 1 hour (bounded: defaults + 25 public custom)
  PUBLIC_PROFILE_LISTS: 60 * 60 * 1000,
  // Inbox unread count - 10 minutes (badge needs to stay fresh)
  INBOX_UNREAD: 10 * 60 * 1000,
  // Inbox threads preview (dropdown) - 1 hour (cache-first UX)
  INBOX_THREADS_PREVIEW: 60 * 60 * 1000,
  // Inbox threads list (first page) - 30 minutes
  INBOX_THREADS: 30 * 60 * 1000,
  // For You page - 1 hour (cache-first hero + shelves, SWR keeps fresh)
  FOR_YOU: 60 * 60 * 1000,
  // Settings (privacy controls) - 1 hour (user-controlled, mutations handle freshness)
  SETTINGS: 60 * 60 * 1000,
  // Notification settings - 1 hour (user-controlled, mutations handle freshness)
  NOTIFICATION_SETTINGS: 60 * 60 * 1000,
  // Blocked users count - 10 minutes (lightweight count only)
  BLOCKED_COUNT: 10 * 60 * 1000,
  // Default fallback (1 hour)
  DEFAULT: 60 * 60 * 1000,
};

/**
 * Granular check if a query should be persisted
 * More specific than domain-only to prevent cache bloat
 */
function shouldPersistQuery(query: Query): boolean {
  const key = query.queryKey;
  if (!Array.isArray(key) || key.length === 0) return false;

  const domain = String(key[0]);

  switch (domain) {
    // Lists: persist ONLY canonical queries (mine, detail, items)
    // DO NOT persist: membership, membershipLookup, optimisticGuards
    case 'lists': {
      if (key.length < 2) return false;

      // ['lists', 'mine', userId] - YES
      if (key[1] === 'mine') return true;

      // ['lists', listId, 'detail'] - YES
      // ['lists', listId, 'items', { sortBy, sortOrder }] - ONLY canonical (position/asc)
      // ['lists', userId, 'public'] - YES (bounded during dehydration)
      if (typeof key[1] === 'string' && key.length >= 3) {
        const subKey = key[2];
        if (subKey === 'detail' || subKey === 'public') return true;
        if (subKey === 'items') {
          // Only persist canonical sort variant (position/asc)
          if (key.length >= 4) {
            const sortParams = key[3] as { sortBy?: string; sortOrder?: string } | undefined;
            if (sortParams && typeof sortParams === 'object') {
              const sb = sortParams.sortBy;
              const so = sortParams.sortOrder;
              return (sb === 'position' && so === 'asc') || (sb === 'added_at' && so === 'desc');
            }
          }
          return true; // No sort params = base items query = canonical
        }
      }

      // ['lists', 'membership', ...] - NO (must refetch on dropdown open)
      // ['lists', 'membershipLookup', ...] - NO
      // ['lists', 'optimisticGuards', ...] - NO (ephemeral)
      return false;
    }

    // Individual rating: persist rating.one ['rating', userId, mediaType, mediaId]
    // NOT ratings.list ['ratings', userId]
    case 'rating':
      return key.length >= 4; // rating.one has 4 elements

    // ratings.list: DO NOT persist (too large, refetch on demand)
    case 'ratings':
      return false;

    // Notifications: only persist unreadCount and dropdown (limit ≤ 10, cursor null)
    case 'notifications':
      // ['notifications', userId, 'unreadCount'] - persist
      if (key.length >= 3 && key[2] === 'unreadCount') {
        return true;
      }
      // ['notifications', userId, 'list', { limit, cursor, type }] - only dropdown
      if (key.length >= 4 && key[2] === 'list') {
        const params = key[3] as { limit?: number; cursor?: string | null } | undefined;
        // Only persist dropdown: limit ≤ 10 AND cursor is null (first page only)
        if (params && typeof params === 'object') {
          const limit = params.limit ?? 100;
          const cursor = params.cursor;
          return limit <= 10 && (cursor === null || cursor === undefined);
        }
      }
      return false;

    // Profiles: persist
    case 'profile':
      return true;

    // Watchlist follows: persist
    case 'watchlists':
    case 'watchlist':
      return true;

    // Media detail pages: persist
    case 'movie':
    case 'tv':
    case 'person':
      return true;

    // Discover: only persist default filters + no search + first page (bounded)
    // ['discover', mediaType, searchQuery | null, params]
    case 'discover':
      if (key.length >= 4) {
        const searchQuery = key[2];
        const params = key[3] as DiscoverParams | undefined;
        // Only persist if no search query AND default filters
        if (searchQuery === null && params && typeof params === 'object') {
          return isDefaultDiscoverParams(params);
        }
      }
      return false;

    // Social feed: only persist first page (cursor=null, limit<=20)
    // ['social', userId, 'feed', { filter, cursor, limit }]
    case 'social':
      if (key.length >= 4 && key[2] === 'feed') {
        const params = key[3] as { cursor?: string | null; limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const cursor = params.cursor;
          const limit = params.limit ?? 20;
          // Only persist first page with bounded limit
          return (cursor === null || cursor === undefined) && limit <= 20;
        }
      }
      return false;

    // Inbox: unreadCount, threadsPreview, threads first page only
    // ['inbox', userId, 'unreadCount'] - YES (10 min maxAge)
    // ['inbox', userId, 'threadsPreview', { limit }] - YES if limit <= 10 (1 hour maxAge)
    // ['inbox', userId, 'threads', { cursor, limit }] - YES if cursor null AND limit <= 20 (30 min maxAge)
    // ['inbox', userId, 'thread', fromUserId, ...] - NO (refetch on view)
    // ['inbox', userId, 'conversation', ...] - NO (refetch on view)
    case 'inbox':
      // ['inbox', userId, 'unreadCount']
      if (key.length >= 3 && key[2] === 'unreadCount') {
        return true;
      }
      // ['inbox', userId, 'threadsPreview', { limit }]
      if (key.length >= 4 && key[2] === 'threadsPreview') {
        const params = key[3] as { limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const limit = params.limit ?? 5;
          return limit <= 10;
        }
        return true; // Default limit is 5
      }
      // ['inbox', userId, 'threads', { cursor, limit }]
      if (key.length >= 4 && key[2] === 'threads') {
        const params = key[3] as { cursor?: string | null; limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const cursor = params.cursor;
          const limit = params.limit ?? 20;
          // Only persist first page with bounded limit
          return (cursor === null || cursor === undefined) && limit <= 20;
        }
      }
      return false;

    // User-specific data: recent searches
    // ['user', userId, 'recentSearches'] - YES (bounded, max 6 items)
    case 'user':
      if (key.length >= 3 && key[2] === 'recentSearches') {
        return true;
      }
      return false;

    // For You page: persist ONLY fully-scoped API key (not apiBase or variants)
    // ['forYou', 'api', userId, tier, mediaType] - YES (exactly 5 elements, trimmed to ≤20 shelves, ≤12 items each)
    case 'forYou':
      if (key.length === 5 && key[1] === 'api') {
        return true;
      }
      return false;

    // Users: followers/following lists (bounded to first page during dehydration)
    // ['users', 'byUsername', usernameLower] - YES (tiny resolver)
    // ['users', profileId, 'followers'|'following', viewerBucket] - YES (trimmed to first page)
    // ['users', profileId, 'followStatus', viewerId] - NO (ephemeral)
    case 'users':
      if (key.length >= 3 && key[1] === 'byUsername') {
        return true; // Username resolver
      }
      if (key.length >= 4 && (key[2] === 'followers' || key[2] === 'following')) {
        return true; // Will be trimmed to first page in filterDehydratedState
      }
      return false;

    // Settings: persist privacy settings
    case 'settings':
      return key.length >= 2; // ['settings', userId]

    // Notification settings: persist
    case 'notification-settings':
      return key.length >= 2; // ['notification-settings', userId]

    // Blocked: only persist count (NOT the infinite list)
    case 'blocked':
      // ['blocked', 'count', userId] - YES
      if (key.length >= 3 && key[1] === 'count') return true;
      // ['blocked', 'list', ...] - NO (modal-only, deferred)
      // ['blocked', 'status', ...] - NO (ephemeral)
      return false;

    default:
      return false;
  }
}

/**
 * Get maxAge for a query based on its type (granular)
 */
function getMaxAgeForQuery(queryKey: QueryKey): number {
  if (!Array.isArray(queryKey) || queryKey.length === 0) {
    return MAX_AGE.DEFAULT;
  }

  const domain = String(queryKey[0]);

  switch (domain) {
    case 'lists':
      // ['lists', userId, 'public'] - 1 hour (bounded profile lists)
      if (queryKey.length >= 3 && queryKey[2] === 'public') {
        return MAX_AGE.PUBLIC_PROFILE_LISTS;
      }
      // ['lists', listId, 'items', ...] - 30 minutes (prevent stale removed-item ghosts)
      if (queryKey.length >= 3 && queryKey[2] === 'items') {
        return MAX_AGE.LISTS_ITEMS;
      }
      return MAX_AGE.LISTS;

    case 'rating':
      return MAX_AGE.RATING_ONE;

    case 'notifications':
      // Split maxAge: unreadCount (10 min) vs dropdown list (1 hour)
      if (queryKey.length >= 3 && queryKey[2] === 'unreadCount') {
        return MAX_AGE.NOTIFICATIONS_UNREAD;
      }
      return MAX_AGE.NOTIFICATIONS_LIST;

    case 'profile':
      return MAX_AGE.PROFILE;

    case 'watchlists':
    case 'watchlist':
      return MAX_AGE.WATCHLIST_FOLLOWS;

    case 'movie':
    case 'tv':
    case 'person':
      return MAX_AGE.MEDIA_DETAIL;

    case 'discover':
      return MAX_AGE.DISCOVER;

    case 'social':
      return MAX_AGE.SOCIAL;

    case 'user':
      return MAX_AGE.RECENT_SEARCHES;

    case 'forYou':
      return MAX_AGE.FOR_YOU;

    case 'users':
      return MAX_AGE.USERS_FOLLOWS;

    case 'inbox':
      // Split maxAge: unreadCount (10 min), threadsPreview (1 hour), threads (30 min)
      if (queryKey.length >= 3 && queryKey[2] === 'unreadCount') {
        return MAX_AGE.INBOX_UNREAD;
      }
      if (queryKey.length >= 4 && queryKey[2] === 'threadsPreview') {
        return MAX_AGE.INBOX_THREADS_PREVIEW;
      }
      return MAX_AGE.INBOX_THREADS;

    case 'settings':
      return MAX_AGE.SETTINGS;

    case 'notification-settings':
      return MAX_AGE.NOTIFICATION_SETTINGS;

    case 'blocked':
      return MAX_AGE.BLOCKED_COUNT;

    default:
      return MAX_AGE.DEFAULT;
  }
}

/**
 * Check if a query's cached data is still valid based on its maxAge
 */
function isQueryDataValid(query: Query, now: number): boolean {
  const maxAge = getMaxAgeForQuery(query.queryKey);
  const dataUpdatedAt = query.state.dataUpdatedAt;

  if (!dataUpdatedAt) return false;

  return now - dataUpdatedAt < maxAge;
}

/**
 * Filter dehydrated state to only include valid, allowlisted queries
 * Uses granular shouldPersistQuery check (not domain-only)
 *
 * DEV-only guard: logs error for any query that would be persisted
 * but doesn't match the allowlist (prevents future drift)
 *
 * CRITICAL: Trims infinite queries (users followers/following) to first page only
 */
function filterDehydratedState(state: DehydratedState): DehydratedState {
  const now = Date.now();
  const isDev = import.meta.env.DEV;

  const filteredQueries = state.queries
    .filter((query) => {
      const isAllowed = shouldPersistQueryKey(query.queryKey);

      // DEV-only guard: log forbidden queries that were about to be persisted
      // This catches drift when someone adds new query shapes without updating allowlist
      if (isDev && !isAllowed) {
        const domain = Array.isArray(query.queryKey) ? String(query.queryKey[0]) : 'unknown';
        // Only warn for domains that LOOK like they might be user-related
        // (avoid noise from discovery/search queries which are correctly excluded)
        const suspiciousDomains = ['rating', 'ratings', 'notifications', 'lists', 'profile', 'watchlist', 'watchlists', 'user', 'users', 'settings', 'notification-settings', 'blocked'];
        if (suspiciousDomains.includes(domain)) {
          console.error(
            `[QueryPersistence] DEV GUARD: Blocked forbidden query from persistence:`,
            JSON.stringify(query.queryKey),
            `\nIf this should be persisted, update shouldPersistQueryKey() in queryPersistence.ts`
          );
        }
      }

      // Granular allowlist check
      if (!isAllowed) {
        return false;
      }

      // Never persist degraded For You timeout payloads — they mask the real cache
      if (
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === 'forYou' &&
        query.queryKey[1] === 'api'
      ) {
        const data = query.state.data as { fallbackReason?: string; cacheHit?: boolean } | undefined;
        if (data && data.fallbackReason && !data.cacheHit) {
          return false;
        }
      }

      // Must have valid data (not expired)
      const maxAge = getMaxAgeForQuery(query.queryKey);
      const dataUpdatedAt = query.state.dataUpdatedAt;

      if (!dataUpdatedAt) return false;

      return now - dataUpdatedAt < maxAge;
    })
    .map((query) => {
      const key = query.queryKey;

      // Trim infinite queries (users followers/following) to first page only
      if (
        Array.isArray(key) &&
        key[0] === 'users' &&
        key.length >= 4 &&
        (key[2] === 'followers' || key[2] === 'following')
      ) {
        const data = query.state.data as { pages?: unknown[]; pageParams?: unknown[] } | undefined;
        if (data?.pages && Array.isArray(data.pages) && data.pages.length > 0) {
          // Keep only first page, trim users to 20
          const firstPage = data.pages[0] as { users?: unknown[] } | undefined;
          const trimmedFirstPage = firstPage?.users
            ? { ...firstPage, users: firstPage.users.slice(0, 20) }
            : firstPage;

          return {
            ...query,
            state: {
              ...query.state,
              data: {
                pages: [trimmedFirstPage],
                pageParams: data.pageParams ? [data.pageParams[0]] : [undefined],
              },
            },
          };
        }
      }

      // Trim public profile lists: keep all defaults + first 25 public custom lists
      if (
        Array.isArray(key) &&
        key[0] === 'lists' &&
        typeof key[1] === 'string' &&
        key.length >= 3 &&
        key[2] === 'public'
      ) {
        const data = query.state.data;
        if (Array.isArray(data)) {
          // Separate defaults (always keep) from public custom lists (cap at 25)
          const defaults = data.filter((item: { is_default?: boolean }) => item.is_default === true);
          const publicCustom = data.filter((item: { is_default?: boolean }) => item.is_default !== true);
          const trimmedPublic = publicCustom.slice(0, 25);

          return {
            ...query,
            state: {
              ...query.state,
              data: [...defaults, ...trimmedPublic],
            },
          };
        }
      }

      // Trim For You API response: keep hero + ≤20 shelves with ≤12 items each
      if (
        Array.isArray(key) &&
        key[0] === 'forYou' &&
        key[1] === 'api'
      ) {
        const data = query.state.data as {
          hero?: unknown;
          shelves?: Array<{ items?: unknown[] }>;
        } | undefined;
        if (data && data.shelves && Array.isArray(data.shelves)) {
          // Trim shelves to max 20, each shelf's items to max 12
          const trimmedShelves = data.shelves.slice(0, 20).map((shelf) => {
            if (shelf.items && Array.isArray(shelf.items)) {
              return { ...shelf, items: shelf.items.slice(0, 12) };
            }
            return shelf;
          });

          return {
            ...query,
            state: {
              ...query.state,
              data: { ...data, shelves: trimmedShelves },
            },
          };
        }
      }

      return query;
    });

  return {
    ...state,
    queries: filteredQueries,
    mutations: [], // Don't persist mutations
  };
}

/**
 * Granular check for dehydrated query (uses queryKey directly)
 * Mirrors shouldPersistQuery but works with just the key
 */
function shouldPersistQueryKey(queryKey: QueryKey): boolean {
  if (!Array.isArray(queryKey) || queryKey.length === 0) return false;

  const domain = String(queryKey[0]);

  switch (domain) {
    // Lists: persist ONLY canonical queries (mine, detail, items)
    // DO NOT persist: membership, membershipLookup, optimisticGuards
    case 'lists': {
      if (queryKey.length < 2) return false;

      // ['lists', 'mine', userId] - YES
      if (queryKey[1] === 'mine') return true;

      // ['lists', listId, 'detail'] - YES
      // ['lists', listId, 'items', { sortBy, sortOrder }] - ONLY canonical (position/asc)
      // ['lists', userId, 'public'] - YES (bounded during dehydration)
      if (typeof queryKey[1] === 'string' && queryKey.length >= 3) {
        const subKey = queryKey[2];
        if (subKey === 'detail' || subKey === 'public') return true;
        if (subKey === 'items') {
          // Only persist canonical sort variant (position/asc)
          if (queryKey.length >= 4) {
            const sortParams = queryKey[3] as { sortBy?: string; sortOrder?: string } | undefined;
            if (sortParams && typeof sortParams === 'object') {
              const sb = sortParams.sortBy;
              const so = sortParams.sortOrder;
              return (sb === 'position' && so === 'asc') || (sb === 'added_at' && so === 'desc');
            }
          }
          return true; // No sort params = base items query = canonical
        }
      }

      // ['lists', 'membership', ...] - NO (must refetch on dropdown open)
      // ['lists', 'membershipLookup', ...] - NO
      // ['lists', 'optimisticGuards', ...] - NO (ephemeral)
      return false;
    }

    case 'rating':
      return queryKey.length >= 4; // rating.one has 4 elements

    case 'ratings':
      return false; // Never persist ratings.list

    case 'notifications':
      if (queryKey.length >= 3 && queryKey[2] === 'unreadCount') {
        return true;
      }
      if (queryKey.length >= 4 && queryKey[2] === 'list') {
        const params = queryKey[3] as { limit?: number; cursor?: string | null } | undefined;
        if (params && typeof params === 'object') {
          const limit = params.limit ?? 100;
          const cursor = params.cursor;
          return limit <= 10 && (cursor === null || cursor === undefined);
        }
      }
      return false;

    case 'profile':
    case 'watchlists':
    case 'watchlist':
    case 'movie':
    case 'tv':
    case 'person':
      return true;

    // Discover: only persist default filters + no search (bounded)
    case 'discover':
      if (queryKey.length >= 4) {
        const searchQuery = queryKey[2];
        const params = queryKey[3] as DiscoverParams | undefined;
        if (searchQuery === null && params && typeof params === 'object') {
          return isDefaultDiscoverParams(params);
        }
      }
      return false;

    // Social feed: only persist first page (cursor=null, limit<=20)
    case 'social':
      if (queryKey.length >= 4 && queryKey[2] === 'feed') {
        const params = queryKey[3] as { cursor?: string | null; limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const cursor = params.cursor;
          const limit = params.limit ?? 20;
          return (cursor === null || cursor === undefined) && limit <= 20;
        }
      }
      return false;

    // Inbox: unreadCount, threadsPreview, threads first page only
    case 'inbox':
      // ['inbox', userId, 'unreadCount']
      if (queryKey.length >= 3 && queryKey[2] === 'unreadCount') {
        return true;
      }
      // ['inbox', userId, 'threadsPreview', { limit }]
      if (queryKey.length >= 4 && queryKey[2] === 'threadsPreview') {
        const params = queryKey[3] as { limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const limit = params.limit ?? 5;
          return limit <= 10;
        }
        return true;
      }
      // ['inbox', userId, 'threads', { cursor, limit }]
      if (queryKey.length >= 4 && queryKey[2] === 'threads') {
        const params = queryKey[3] as { cursor?: string | null; limit?: number } | undefined;
        if (params && typeof params === 'object') {
          const cursor = params.cursor;
          const limit = params.limit ?? 20;
          return (cursor === null || cursor === undefined) && limit <= 20;
        }
      }
      return false;

    // User-specific data: recent searches
    case 'user':
      if (queryKey.length >= 3 && queryKey[2] === 'recentSearches') {
        return true;
      }
      return false;

    // For You page: persist ONLY fully-scoped API key (not apiBase or variants)
    // ['forYou', 'api', userId, tier, mediaType] - YES (exactly 5 elements, trimmed to ≤20 shelves, ≤12 items each)
    case 'forYou':
      if (queryKey.length === 5 && queryKey[1] === 'api') {
        return true;
      }
      return false;

    // Users: followers/following lists (bounded to first page during dehydration)
    case 'users':
      if (queryKey.length >= 3 && queryKey[1] === 'byUsername') {
        return true; // Username resolver
      }
      if (queryKey.length >= 4 && (queryKey[2] === 'followers' || queryKey[2] === 'following')) {
        return true; // Will be trimmed to first page in filterDehydratedState
      }
      return false;

    // Settings: persist privacy settings
    case 'settings':
      return queryKey.length >= 2;

    // Notification settings: persist
    case 'notification-settings':
      return queryKey.length >= 2;

    // Blocked: only persist count (NOT the infinite list)
    case 'blocked':
      if (queryKey.length >= 3 && queryKey[1] === 'count') return true;
      return false;

    default:
      return false;
  }
}

/**
 * Estimate the size of data in bytes (rough approximation)
 */
function estimateSize(data: unknown): number {
  return new Blob([JSON.stringify(data)]).size;
}

/**
 * Trim queries to stay within bounds
 * Removes oldest queries first until within limits
 */
function trimToSizeBounds(state: DehydratedState): DehydratedState {
  let queries = [...state.queries];

  // First, check entry count
  if (queries.length > MAX_ENTRIES) {
    // Sort by dataUpdatedAt (oldest first) and trim
    queries.sort((a, b) => {
      const aTime = a.state.dataUpdatedAt ?? 0;
      const bTime = b.state.dataUpdatedAt ?? 0;
      return aTime - bTime;
    });
    queries = queries.slice(-MAX_ENTRIES);
  }

  // Then check size (iteratively remove oldest until under limit)
  let currentSize = estimateSize({ ...state, queries });
  while (currentSize > MAX_SIZE_BYTES && queries.length > 0) {
    // Sort and remove oldest
    queries.sort((a, b) => {
      const aTime = a.state.dataUpdatedAt ?? 0;
      const bTime = b.state.dataUpdatedAt ?? 0;
      return aTime - bTime;
    });
    queries = queries.slice(1);
    currentSize = estimateSize({ ...state, queries });
  }

  return {
    ...state,
    queries,
  };
}

/**
 * Create a user-scoped IndexedDB store
 */
function createUserStore(userId: string): UseStore {
  const dbName = `filmple-cache-${userId}`;
  return createStore(dbName, 'query-cache');
}

/**
 * IndexedDB key for persisted client
 */
const CACHE_KEY = 'persisted-client';

/**
 * Create an IndexedDB persister for a specific user
 */
export function createIndexedDbPersister(userId: string): Persister {
  const store = createUserStore(userId);

  return {
    persistClient: async (client: PersistedClient) => {
      // Filter and trim before persisting
      const filteredState = filterDehydratedState(client.clientState);
      const trimmedState = trimToSizeBounds(filteredState);

      const persistedClient: PersistedClient = {
        ...client,
        clientState: trimmedState,
        buster: `v${PERSISTENCE_VERSION}`,
      };

      try {
        await set(CACHE_KEY, persistedClient, store);
      } catch (err) {
        console.error('[QueryPersistence] Failed to persist cache:', err);
        // On quota error, try to clear and save minimal state
        if (err instanceof Error && err.name === 'QuotaExceededError') {
          try {
            await del(CACHE_KEY, store);
          } catch {
            // Ignore delete errors
          }
        }
      }
    },

    restoreClient: async (): Promise<PersistedClient | undefined> => {
      try {
        const client = await get<PersistedClient>(CACHE_KEY, store);

        // Validate version
        if (client && client.buster !== `v${PERSISTENCE_VERSION}`) {
          console.log('[QueryPersistence] Cache version mismatch, clearing');
          await del(CACHE_KEY, store);
          return undefined;
        }

        return client;
      } catch (err) {
        console.error('[QueryPersistence] Failed to restore cache:', err);
        return undefined;
      }
    },

    removeClient: async () => {
      try {
        await del(CACHE_KEY, store);
      } catch (err) {
        console.error('[QueryPersistence] Failed to remove cache:', err);
      }
    },
  };
}

/**
 * Clear all cached data for a user (call on logout)
 */
export async function clearUserCache(userId: string): Promise<void> {
  try {
    const store = createUserStore(userId);
    await del(CACHE_KEY, store);
    console.log('[QueryPersistence] Cleared cache for user:', userId);
  } catch (err) {
    console.error('[QueryPersistence] Failed to clear user cache:', err);
  }
}

/**
 * Export persistence config for use with manual hydration
 */
export const persistenceConfig = {
  version: PERSISTENCE_VERSION,
  maxEntries: MAX_ENTRIES,
  maxSizeBytes: MAX_SIZE_BYTES,
  maxAge: MAX_AGE,
};

/**
 * Dehydrate options for filtering what gets persisted
 */
export const dehydrateOptions = {
  shouldDehydrateQuery: shouldPersistQuery,
};

/**
 * Check if persisted data for a query is expired
 * Used during rehydration to skip expired entries
 */
export { isQueryDataValid, getMaxAgeForQuery };
